import { Controller } from '@hotwired/stimulus';

const CANVAS_HEIGHT = 176;
const STROKE_COLOR = '#111827';
const STROKE_WIDTH = 2;

export default class extends Controller {
  static targets = ['canvas', 'input', 'placeholder'];

  connect() {
    this.isDrawing = false;
    this.hasStroke = false;
    this.lastPoint = null;
    this.currentWidth = 0;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);

    this.resizeCanvas();
    this.addCanvasListeners();

    this.resizeObserver = new ResizeObserver(() => {
      const newWidth = Math.floor(this.canvasTarget.getBoundingClientRect().width);
      if (newWidth > 0 && newWidth !== this.currentWidth) {
        this.resizeCanvas();
      }
    });
    this.resizeObserver.observe(this.canvasTarget);

    const existingData = this.inputTarget.value;
    if (existingData && existingData.length > 0) {
      this.restoreFromDataUrl(existingData);
    } else {
      this.showPlaceholder();
    }
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    this.removeCanvasListeners();
  }

  clear(event) {
    event?.preventDefault();
    this.clearCanvas();
    this.inputTarget.value = '';
    this.showPlaceholder();
  }

  hidePlaceholder() {
    if (!this.hasPlaceholderTarget) return;

    this.placeholderTarget.classList.add('hidden');
  }

  showPlaceholder() {
    if (!this.hasPlaceholderTarget) return;

    this.placeholderTarget.classList.remove('hidden');
  }

  resizeCanvas() {
    const previousData = this.currentReviewData();
    const ratio = this.deviceRatio();
    const width = Math.max(Math.floor(this.canvasTarget.getBoundingClientRect().width), 1);
    this.currentWidth = width;

    this.canvasTarget.width = width * ratio;
    this.canvasTarget.height = CANVAS_HEIGHT * ratio;

    this.ctx = this.canvasTarget.getContext('2d');
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.ctx.strokeStyle = STROKE_COLOR;
    this.ctx.lineWidth = STROKE_WIDTH;

    this.clearCanvas();

    if (previousData?.length > 0) {
      this.restoreFromDataUrl(previousData);
    }
  }

  currentReviewData() {
    return this.inputTarget.value;
  }

  syncInputWithPad() {
    if (!this.hasStroke) {
      this.inputTarget.value = '';
      this.showPlaceholder();
      this.dispatch('sync', { detail: { data: '' } });
      return;
    }

    const dataUrl = this.canvasTarget.toDataURL('image/png');
    this.inputTarget.value = dataUrl;
    this.hidePlaceholder();
    this.dispatch('sync', { detail: { data: dataUrl } });
  }

  restoreFromDataUrl(dataUrl) {
    if (!dataUrl) {
      this.showPlaceholder();
      return;
    }

    const image = new Image();
    image.onload = () => {
      this.clearCanvas();
      this.ctx.drawImage(image, 0, 0, this.canvasTarget.width / this.deviceRatio(), CANVAS_HEIGHT);
      this.hasStroke = true;
      this.syncInputWithPad();
    };

    image.onerror = () => {
      this.clearCanvas();
      this.showPlaceholder();
    };

    image.src = dataUrl;
  }

  addCanvasListeners() {
    this.canvasTarget.addEventListener('pointerdown', this.onPointerDown);
    this.canvasTarget.addEventListener('pointermove', this.onPointerMove);
    this.canvasTarget.addEventListener('pointerup', this.onPointerUp);
    this.canvasTarget.addEventListener('pointerleave', this.onPointerUp);
    this.canvasTarget.addEventListener('pointercancel', this.onPointerUp);
  }

  removeCanvasListeners() {
    this.canvasTarget.removeEventListener('pointerdown', this.onPointerDown);
    this.canvasTarget.removeEventListener('pointermove', this.onPointerMove);
    this.canvasTarget.removeEventListener('pointerup', this.onPointerUp);
    this.canvasTarget.removeEventListener('pointerleave', this.onPointerUp);
    this.canvasTarget.removeEventListener('pointercancel', this.onPointerUp);
  }

  onPointerDown(event) {
    event.preventDefault();
    this.canvasTarget.setPointerCapture(event.pointerId);
    this.isDrawing = true;
    this.lastPoint = this.toCanvasPoint(event);
    this.hasStroke = true;
    this.hidePlaceholder();
  }

  onPointerMove(event) {
    if (!this.isDrawing) return;

    const currentPoint = this.toCanvasPoint(event);
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastPoint.x, this.lastPoint.y);
    this.ctx.lineTo(currentPoint.x, currentPoint.y);
    this.ctx.stroke();
    this.lastPoint = currentPoint;
  }

  onPointerUp(event) {
    if (!this.isDrawing) return;

    this.isDrawing = false;
    this.lastPoint = null;
    this.canvasTarget.releasePointerCapture(event.pointerId);
    this.syncInputWithPad();
  }

  toCanvasPoint(event) {
    const rect = this.canvasTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  clearCanvas() {
    this.ctx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height);
    this.hasStroke = false;
  }

  deviceRatio() {
    return Math.max(window.devicePixelRatio || 1, 1);
  }
}
